import styled from '@emotion/styled';
import {
  Prismic,
  PrismicLink,
  fallbackText,
  getDataFromLink,
  PrismicContext,
} from '@landsbankinn/prismic';
import { Button, theme, SliderListItem } from '@landsbankinn/ui';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useContext } from 'react';
import { SubItem } from '../components/subnav/MobileSubnav';
import { Subnav } from '../components/subnav/Subnav';

interface Item {
  link: Prismic.Link;
  link_text: Prismic.RichText;
}

interface SubnavContainerProps {
  page: {
    page_title: Prismic.RichText;
    _meta: any;
    sub_navigation: Item[];
  };
  active: any;
  ctaLink?: Prismic.Link;
  ctaText?: Prismic.RichText;
  ctaMobileText?: Prismic.RichText;
  isTransparent?: boolean;
  titleWidth?: string;
  className?: string;
}

const StyledA = styled.a`
  @media (min-width: 1400px) {
    white-space: nowrap;
  }
`;

const CtaButton = styled(Button)<any>`
  min-width: ${(props) => (props.mobile ? 'auto' : '145px;')};
  min-height: 45px;
  padding: 0 36px;
`;

export function SubnavContainer({
  page,
  active,
  ctaLink,
  ctaText,
  ctaMobileText,
  isTransparent,
  titleWidth,
  className,
}: SubnavContainerProps) {
  const router = useRouter();
  const { urlResolver } = useContext(PrismicContext);
  const items = (page.sub_navigation || []).map((item) => {
    const link = Prismic.link(urlResolver, item?.link, item?.link_text);
    if (!link) return null;
    const id = (item?.link as any)?._meta?.id;
    const isActive = id === active?._meta?.id || link.as === router.asPath;
    return { item, id, active: isActive, ...link };
  });

  const linkdata = getDataFromLink(ctaLink);
  const buttonTitle = fallbackText(ctaText as any, linkdata.title ?? undefined);

  const action = ctaLink ? (
    <PrismicLink value={ctaLink} title={buttonTitle ?? undefined}>
      <CtaButton as={'a'} title={buttonTitle} bg={theme.colors.primary} />
    </PrismicLink>
  ) : null;

  const mobileButtonTitle = fallbackText(
    ctaMobileText as any,
    linkdata.title ?? undefined,
  );

  const mobileAction =
    ctaLink && ctaMobileText ? (
      <PrismicLink value={ctaLink} title={mobileButtonTitle ?? undefined}>
        <CtaButton
          as={'a'}
          title={mobileButtonTitle}
          mobile={true}
          bg={theme.colors.secondary}
        />
      </PrismicLink>
    ) : null;

  const activeTitle =
    active === undefined
      ? Prismic.text(page?.page_title)
      : active.page_title
      ? Prismic.text(active?.page_title)
      : items.find((item) => item?.active)?.title;

  return (
    <Subnav
      titleWidth={titleWidth}
      title={
        <Link href={urlResolver(page?._meta).as as any}>
          <a>{Prismic.text(page.page_title)}</a>
        </Link>
      }
      pageTitle={activeTitle ?? undefined}
      cta={action}
      mobilecta={mobileAction}
      isTransparent={isTransparent}
      className={className}
      mobileItems={
        <>
          <SubItem
            key="parent"
            active={items.find((item) => item?.active) === undefined}
          >
            <Link href={urlResolver(page?._meta).as as any}>
              <a>{Prismic.text(page.page_title)}</a>
            </Link>
          </SubItem>
          {items
            .filter((x) => !!x?.item.link)
            .map((item, index) => (
              <SubItem
                key={index}
                active={item?.active}
                aria-current={item?.active ? 'page' : 'false'}
              >
                <Link href={item?.as as any}>
                  <a>{item?.title}</a>
                </Link>
              </SubItem>
            ))}
        </>
      }
    >
      {items
        .filter((x) => !!x?.item.link)
        .map((item, index) => (
          <SliderListItem
            key={index}
            active={item?.active}
            aria-current={item?.active ? 'page' : 'false'}
          >
            <Link href={item?.as as any}>
              <StyledA>{item?.title}</StyledA>
            </Link>
          </SliderListItem>
        ))}
    </Subnav>
  );
}
